import { useCreateContractStore } from "./createContractStore";
import { useCreateBPFStore } from "./createBPFStore";
import { useCreateOutcomeStore } from "./createOutcomeStore";
import { useUploadClaimFileStore } from "./uploadClaimFileStore";
import { isArrayWithContent } from "~/utils/array";
import { useSelectedCountry } from "~/hooks";

type Conditions = {
    [key: string]: {
        amount: string;
        selectedCondition: string;
    };
};

const mapContractForApi = ({
    generalInformation,
    productsDetails,
    contractMilestones,
    contractReview,
    financialConditions,
    contractPartners,
    contractModel,
    contractCategories,
    scales,
    contractCompensation,
    isBelgiumTeam,
}: any) => {
    const isVolumeBased = !!isArrayWithContent(contractCategories);

    const mappedFinancialConditions = () => {
        if (
            isVolumeBased ||
            Object.keys(financialConditions?.conditions as Conditions)
                .length === 0
        )
            return;

        const selectedCondition = Object.values(
            financialConditions?.conditions as Conditions,
        )[0]?.selectedCondition;
        const amount = Object.values(
            financialConditions?.conditions as Conditions,
        )[0]?.amount;

        return {
            [Object.keys(financialConditions?.conditions as Conditions)[0]]: {
                [selectedCondition]: amount,
            },
        };
    };

    const mapProducts = () => {
        if (!isArrayWithContent(productsDetails?.products)) return [];

        return productsDetails.products.map((item: any) => {
            return {
                datamodelId: contractModel?.datamodelId,
                productId: item?.product?.productId,
                priceSource: item?.priceSource || undefined,
                priceCondition:
                    item?.priceCondition?.priceCondition || undefined,
                milestones: !isVolumeBased
                    ? contractMilestones?.milestones
                    : undefined,
                financialConditions: mappedFinancialConditions(),
                discountPercentage:
                    item?.type === "discountPercentage"
                        ? item?.amount
                        : undefined,
                contractPrice:
                    item?.type === "contractPrice" ? item?.amount : undefined,
            };
        });
    };
    const mapPartners = () => {
        if (!isArrayWithContent(contractPartners)) return [];
        return contractPartners.map((partner: { id: string }) => partner.id);
    };

    return Object.assign(
        {},
        {
            contractType: isVolumeBased ? "VOLUME" : "PERFORMANCE",
            categories: contractCategories,
            datamodelId: contractModel?.datamodelId,
            country: generalInformation?.country?.isoCode,
            startDate: generalInformation?.startDate,
            endDate: generalInformation?.endDate,
            brandId: isVolumeBased
                ? undefined
                : generalInformation?.brand?.brandId,
            reference: generalInformation?.reference,
            externalReference: generalInformation?.externalReference,
            primaryPartnerId: generalInformation?.primaryPartner?.accountId,
            icdNo: generalInformation?.icdNo,
            responsible: generalInformation?.responsible,
            therapeuticAreaId:
                generalInformation?.therapeuticArea?.therapeuticAreaId,
            indicationId: isVolumeBased
                ? undefined
                : generalInformation?.indication?.indicationId,
            additionalPartners: mapPartners(),
            products: mapProducts(),
            comments: contractReview?.comments,
            icdUrl: generalInformation?.icdUrl,
            claimPeriodicity: isVolumeBased
                ? generalInformation?.claimPeriodicity?.id
                : undefined,
            scales: isVolumeBased && isBelgiumTeam ? scales : undefined,
            compensations:
                isVolumeBased && isBelgiumTeam
                    ? contractCompensation
                    : undefined,
        },
    );
};

// Hook for create contract mapper (replaces the Store state selector)
export const useCreateContractMapper = () => {
    const { isBelgiumTeam } = useSelectedCountry();
    const {
        generalInformation,
        productsDetails,
        contractMilestones,
        contractReview,
        financialConditions,
        contractPartners,
        selectModel: contractModel,
        selectCategories: contractCategories,
        resetAllContractData,
        scales,
        contractCompensation,
    } = useCreateContractStore();

    const getContractData = () => {
        return mapContractForApi({
            generalInformation,
            productsDetails,
            contractMilestones,
            contractReview,
            financialConditions,
            contractPartners,
            contractModel,
            contractCategories,
            scales,
            contractCompensation,
            isBelgiumTeam,
        });
    };

    const resetContractData = () => {
        resetAllContractData();
    };

    return {
        getContractData,
        resetContractData,
    };
};

const mapBPFForApi = ({
    generalInformation,
    paymentInformation,
    selectClaims,
    selectOutcomes,
}: any) => {
    const isVolumeBased = isArrayWithContent(selectClaims);
    return {
        ...generalInformation,
        ...paymentInformation,
        bankType: paymentInformation?.bankType || null,
        contracts: isVolumeBased
            ? selectClaims.map((item: any) => {
                  return {
                      claims: item?.claims,
                      contractId: item?.contractId,
                  };
              })
            : selectOutcomes?.map((item: any) => {
                  return {
                      outcomes: item?.outcomes,
                      contractId: item?.contractId,
                  };
              }),
        contractPartner: undefined, // For UI purpose only
        paymentCondition: undefined, // For UI purpose only
    };
};

export const useCreateBPFMapper = () => {
    const {
        selectClaims,
        selectContracts,
        selectOutcomes,
        generalInformation,
        paymentInformation,
        resetBPFData,
    } = useCreateBPFStore();

    const getBPFData = () => {
        return mapBPFForApi({
            selectClaims,
            selectContracts,
            selectOutcomes,
            generalInformation,
            paymentInformation,
        });
    };

    return {
        getBPFData,
        resetBPFData,
    };
};

const mapOutcomeForApi = (states: any) =>
    Object.assign(
        {},
        {
            contractId: states.contractSelection.contractId,
            orderId: states.orderSelection.orderId,
            outcomeVariables: states.outcomeDetails,
        },
    );

export const useCreateOutcomeMapper = () => {
    const {
        contractSelection,
        orderSelection,
        outcomeDetails,
        resetOutcomeData,
    } = useCreateOutcomeStore();

    const getOutcomeData = () => {
        return mapOutcomeForApi({
            contractSelection,
            orderSelection,
            outcomeDetails,
        });
    };

    return {
        getOutcomeData,
        resetOutcomeData,
    };
};

const mapClaimForApi = (states: any) =>
    Object.assign(
        {},
        {
            contractId: states.contractSelection.contractId,
            orderId: states.orderSelection.orderId,
            outcomeVariables: states.outcomeDetails,
        },
    );

export const useUploadClaimProcessMapper = () => {
    const { selectContract, claimDetails, resetUploadClaimData } =
        useUploadClaimFileStore();

    const getUploadClaimData = () => {
        return mapClaimForApi({
            selectContract,
            claimDetails,
        });
    };

    return {
        getUploadClaimData,
        resetUploadClaimData,
    };
};
